// calls-page-isolation — the #/calls paginated view OWNS its list state.
// (docs/REGRESSIONS.md 2026-07-02.)
//
// The 2026-07 regression: on /#/calls, clicking page 2/3/4 highlighted the new
// page number but the ROWS NEVER CHANGED — page 2 rendered page-1 rows. Root
// cause: the docked-phone poller (phone-panel-calls-voicemail, 60s) SHARED
// callsData/callsTotal/callsPageCount with the Calls view and wrote its own
// fixed page-1/limit-20 fetch into them. That write was VESTIGIAL — the phone's
// Recents surface and missed-call badges read a SEPARATE state (deviceCallLog,
// fed by the phone-device-call-log poller), never callsData. The 2026-07-01
// activeViewRef runtime guard tried to gate the shared write by the live view,
// but gating a shared write on async view-timing is inherently race-prone; the
// durable fix REMOVES the shared write so the phone poller can never touch the
// view state under ANY timing.
//
// This module is the runtime half of the guard: the #/calls fetch effect tags
// every response with the query it fired for and applies it to the view state
// ONLY when that tag still equals the view's CURRENT query. A stale in-flight
// response, or any foreign-query write (e.g. a page-1/limit-20 poller fetch),
// is DROPPED — page N can never render another query's rows. The structural
// half (the poller region must contain NO view-state write) lives in
// callsPaginationGuard.ts.

/** Everything that defines WHICH calls the #/calls view is currently showing.
 *  Two responses are interchangeable iff every field here matches. */
export type CallsViewQuery = {
  page: number
  limit: number
  search: string
  direction: string
  outcome: string
  hasRecording: boolean
  hasTranscript: boolean
  sortField: string
  sortOrder: string
}

/** Stable, order-fixed serialization of a query — the response "tag". Booleans
 *  are normalized to 0/1 and strings pass through, so `''` (no filter) and a
 *  real value never collide. */
export function callsQueryKey(q: CallsViewQuery): string {
  return [
    q.page,
    q.limit,
    q.search,
    q.direction,
    q.outcome,
    q.hasRecording ? 1 : 0,
    q.hasTranscript ? 1 : 0,
    q.sortField,
    q.sortOrder,
  ].join('|')
}

/** The ownership rule: a fetched response may be applied to the view state ONLY
 *  when the query it was fetched for still matches the view's current query.
 *  Anything else (stale page, changed filter/sort/limit, a foreign page-1/
 *  limit-20 poller fetch) must be dropped. */
export function shouldApplyCallsResponse(current: CallsViewQuery, fetched: CallsViewQuery): boolean {
  return callsQueryKey(current) === callsQueryKey(fetched)
}
